/**
 * Original, deterministic SVG artwork for course cards — themed by course
 * family (privacy, security, risk, quality, …) with per-course variation
 * derived from the course code so every card looks distinct.
 */

import type { ReactElement } from 'react';

interface Theme {
  from: string;
  to: string;
  icon: ReactElement;
}

const ICONS = {
  award: (
    <g fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="24" cy="19" r="9" />
      <path d="M19 26l-3 12 8-5 8 5-3-12" />
      <path d="M24 15v4l3 2" />
    </g>
  ),
  shield: (
    <g fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M24 6l14 6v10c0 9-6 15.5-14 20-8-4.5-14-11-14-20V12l14-6z" />
      <path d="M18 24l4.5 4.5L31 20" />
    </g>
  ),
  lock: (
    <g fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="13" y="21" width="22" height="17" rx="3" />
      <path d="M17 21v-5a7 7 0 0 1 14 0v5" />
      <circle cx="24" cy="29" r="2.5" fill="#fff" stroke="none" />
    </g>
  ),
  ribbon: (
    <g fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="24" cy="24" r="15" />
      <path d="M16.5 24.5l5 5L32 19" />
    </g>
  ),
  gauge: (
    <g fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 33a15 15 0 1 1 30 0" />
      <path d="M24 33l8-10" />
      <circle cx="24" cy="33" r="2.5" fill="#fff" stroke="none" />
    </g>
  ),
} as const;

function themeFor(code: string): Theme {
  if (code.startsWith('privacy')) return { from: '#7c3aed', to: '#4f46e5', icon: ICONS.lock };
  if (code.startsWith('security')) return { from: '#e11d48', to: '#9f1239', icon: ICONS.shield };
  if (code.startsWith('risk')) return { from: '#d97706', to: '#b45309', icon: ICONS.gauge };
  if (code.startsWith('quality')) return { from: '#059669', to: '#047857', icon: ICONS.ribbon };
  if (code.startsWith('certification')) return { from: '#0ea5e9', to: '#0369a1', icon: ICONS.award };
  return { from: '#4338ca', to: '#312e81', icon: ICONS.shield };
}

function hashCode(code: string): number {
  let h = 0;
  for (let i = 0; i < code.length; i++) h = (h * 31 + code.charCodeAt(i)) >>> 0;
  return h;
}

export function CourseArt({ code, title }: { code: string; title: string }) {
  const theme = themeFor(code);
  const h = hashCode(code);
  const gradientId = `course-art-${code}`;
  // Per-course variation: three floating circles positioned by the hash.
  const circles = [0, 1, 2].map((i) => {
    const cx = 20 + ((h >>> (i * 5)) % 120);
    const cy = 8 + ((h >>> (i * 7)) % 64);
    const r = 10 + ((h >>> (i * 3)) % 18);
    return <circle key={i} cx={cx} cy={cy} r={r} fill="#fff" opacity="0.09" />;
  });

  return (
    <svg
      className="course-art"
      viewBox="0 0 160 80"
      role="img"
      aria-label={`${title} course artwork`}
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={theme.from} />
          <stop offset="1" stopColor={theme.to} />
        </linearGradient>
      </defs>
      <rect width="160" height="80" fill={`url(#${gradientId})`} />
      {circles}
      <g transform="translate(56 16) scale(1)">{theme.icon}</g>
    </svg>
  );
}
